import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AuditEventResponse,
  type CostCoverage,
  type EntryResponse,
  type EntryValue,
  BASE_PRICE_LIST,
  type BatchEntriesRequest,
  italianToday,
  listEntries,
  PRICE_LISTS_TABLE,
  priceListBinding,
  type MoveDirection,
  moveEntry,
  entriesToCsv,
  orderGroupOf,
  parentColumn,
  systemTable,
  TABLE_GROUPS,
  tableWriteRequires,
  type TableColumn,
  type TableResponse,
  type TableSummary,
  VERSION_STATE_LABELS,
  type VersionResponse,
  type VersionState,
} from '@k-management/shared';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DataGrid, type GridColumn, type GridGroups } from '../../components/DataGrid.js';
import { Field } from '../../components/Field.js';
import { Icon } from '../../components/Icon.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { ImportEntriesDialog } from './ImportEntriesDialog.js';
import { apiRequest } from '../../lib/api.js';
import { describeAuditDetails } from '../../lib/audit-format.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { usePreference } from '../../lib/preferences.js';
import { coverageLabel, LinkedCosts, type TypeLevel } from './cost-coverage-ui.js';
import { EntryDialog } from './EntryDialog.js';
import { GridCellInput, moveOnEnter } from './grid-edit-ui.js';
import { PriceListBar } from './price-lists-ui.js';
import { NewTableDialog } from './NewTableDialog.js';
import { compareEntries, downloadText, formatValue } from './table-format.js';

/** What was changed in a voice of the grid unlocked, not saved yet. */
interface CellEdits {
  description?: string;
  values?: Record<string, EntryValue>;
}

/** A voice added in the grid unlocked, not saved yet. */
interface NewRow {
  key: string;
  code: string;
  description: string;
  values: Record<string, EntryValue>;
}

/** The table whose voices the table Costi points to (decision of 29/09/2026). */
const ROW_TYPES = 'tipologie-riga';

const STATE_CLASS: Record<VersionState, string> = {
  draft: 'pill warn',
  scheduled: 'pill info',
  active: 'pill ok',
  archived: 'pill muted',
};

/**
 * Tabelle (proposal §5): selector of the tables, entries of the chosen version
 * in the common grid, versions with comparison and activation from a date.
 */
export function TablesPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const tables = useQuery({
    queryKey: ['tables'],
    queryFn: () => apiRequest<TableSummary[]>('/tables'),
  });

  useEffect(() => {
    if (!key && tables.data?.[0]) navigate(`/tabelle/${tables.data[0].key}`, { replace: true });
  }, [key, tables.data, navigate]);

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Tabelle</h1>
          <p className="muted">
            Le tabelle di riferimento dei moduli: codici stabili, descrizioni modificabili, versioni
            con data di validità e storico.
          </p>
        </div>
      </div>
      {tables.isPending && <p className="muted">Caricamento…</p>}
      {tables.isError && <p className="error">{tables.error.message}</p>}
      {tables.data && (
        <div className="tables-layout">
          <TableSelector tables={tables.data} current={key} />
          {key && <TableView key={key} tableKey={key} />}
        </div>
      )}
    </section>
  );
}

function TableSelector({ tables, current }: { tables: TableSummary[]; current?: string }) {
  const [query, setQuery] = useState('');
  const canCreate = useCan('tabelle.admin');
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const needle = query.trim().toLowerCase();
  // Groups open or closed as the user left them; the one of the table shown opens by itself.
  const [open, setOpen] = usePreference<string[]>('tables.groups', []);
  const currentGroup = tables.find((table) => table.key === current)?.group;
  useEffect(() => {
    if (currentGroup && !open.includes(currentGroup)) setOpen([...open, currentGroup]);
    // Only when the table shown changes group: closing it by hand stays closed.
  }, [currentGroup]);
  const toggle = (group: string) =>
    setOpen(open.includes(group) ? open.filter((item) => item !== group) : [...open, group]);

  return (
    <nav className="card table-selector" aria-label="Tabelle">
      <label className="grid-search">
        <Icon name="search" size={16} />
        <input
          type="search"
          placeholder="Cerca tabella"
          aria-label="Cerca tabella"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {TABLE_GROUPS.map((group) => {
        const items = tables.filter(
          (table) =>
            table.group === group && (!needle || table.name.toLowerCase().includes(needle)),
        );
        if (items.length === 0 && !(group === 'Tabelle libere' && canCreate && !needle)) {
          return null;
        }
        // A search opens every group with a match.
        const expanded = Boolean(needle) || open.includes(group);
        return (
          <div key={group} className="table-group">
            <button
              type="button"
              className="table-group-label"
              aria-expanded={expanded}
              onClick={() => toggle(group)}
            >
              <Icon name="right" size={14} />
              {group}
              <span className="muted">{items.length}</span>
            </button>
            {expanded && (
              <>
                {items.map((table) => (
                  <Link
                    key={table.key}
                    to={`/tabelle/${table.key}`}
                    className={table.key === current ? 'table-link selected' : 'table-link'}
                  >
                    {table.name}
                    {table.hasDraft && <span className="dot" title="Versione in bozza" />}
                  </Link>
                ))}
                {group === 'Tabelle libere' && canCreate && (
                  <button type="button" className="link" onClick={() => setCreating(true)}>
                    + Nuova tabella libera
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}
      {creating && (
        <NewTableDialog
          onClose={() => setCreating(false)}
          onCreated={(table) => {
            setCreating(false);
            navigate(`/tabelle/${table.key}`);
          }}
        />
      )}
    </nav>
  );
}

function TableView({ tableKey }: { tableKey: string }) {
  const queryClient = useQueryClient();
  // A table changed only with its own permission (the company roles) is read-only without it.
  const writeRequired = tableWriteRequires(tableKey);
  const canWriteTable = useCan(...(writeRequired ? [writeRequired] : []));
  const canWrite = useCan('tabelle.write') && canWriteTable;
  const canAdmin = useCan('tabelle.admin') && canWriteTable;
  const canCosts = useCan('preventivatore.costs');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const table = useQuery({
    queryKey: ['tables', tableKey],
    queryFn: () => apiRequest<TableResponse>(`/tables/${tableKey}`),
  });
  const [shownNumber, setShownNumber] = useState<number | null>(null);
  const [compareWith, setCompareWith] = useState<number | null>(null);
  const [editing, setEditing] = useState<EntryResponse | 'new' | null>(null);
  // Values of a new entry opened from another table («Aggiungi costo»).
  const [prefill, setPrefill] = useState<Record<string, EntryValue> | null>(null);
  const [prefillDescription, setPrefillDescription] = useState<string | null>(null);
  // The list shown in a table of voices of the price lists; the Base at first.
  const binding = priceListBinding(tableKey);
  const [chosenList, setChosenList] = useState<string | null>(null);
  const currentList = binding ? (chosenList ?? BASE_PRICE_LIST[binding.kind]) : null;
  const isBaseList = binding !== undefined && currentList === BASE_PRICE_LIST[binding.kind];
  const [activating, setActivating] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showVersions, setShowVersions] = useState(true);
  // The grid unlocked (decision of 01/10/2026): cells change in place and are saved together.
  const [gridEdit, setGridEdit] = useState(false);
  const [edits, setEdits] = useState<Record<string, CellEdits>>({});
  const [newRows, setNewRows] = useState<NewRow[]>([]);
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const data = table.data;
  const shown =
    shownNumber ??
    data?.editableVersion ??
    data?.activeVersion ??
    data?.versions[0]?.number ??
    null;
  const entries = useQuery({
    queryKey: ['tables', tableKey, 'entries', shown],
    queryFn: () => apiRequest<EntryResponse[]>(`/tables/${tableKey}/versions/${shown}/entries`),
    enabled: shown !== null,
  });
  const baseEntries = useQuery({
    queryKey: ['tables', tableKey, 'entries', compareWith],
    queryFn: () =>
      apiRequest<EntryResponse[]>(`/tables/${tableKey}/versions/${compareWith}/entries`),
    enabled: compareWith !== null,
  });

  // The costs of every type: reserved data, asked only by who may see them.
  const coverage = useQuery({
    queryKey: ['tables', tableKey, 'costs', shown],
    queryFn: () =>
      apiRequest<Record<string, CostCoverage>>(`/tables/${ROW_TYPES}/versions/${shown}/costs`),
    enabled: tableKey === ROW_TYPES && canCosts && shown !== null,
  });

  usePageTitle(data?.name ?? null);

  const editable = canWrite && data !== undefined && shown === data.editableVersion;

  // «?nuova=1&type=…»: opens a new entry with those values, then forgets them.
  useEffect(() => {
    if (!data || !editable || params.get('nuova') === null) return;
    const values: Record<string, EntryValue> = {};
    for (const column of data.columns) {
      const value = params.get(column.key);
      if (value) values[column.key] = value;
    }
    setPrefill(values);
    setEditing('new');
    setParams({}, { replace: true });
  }, [data, editable, params, setParams]);
  const hasDraft = data?.versions.some((version) => version.state === 'draft') ?? false;

  const newVersion = useMutation({
    mutationFn: (from: number) =>
      apiRequest<TableResponse>(`/tables/${tableKey}/versions`, { method: 'POST', body: { from } }),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      setShownNumber(updated.editableVersion);
      setCompareWith(shown);
    },
  });
  const move = useMutation({
    mutationFn: ({ code, direction }: { code: string; direction: MoveDirection }) =>
      apiRequest<EntryResponse[]>(
        `/tables/${tableKey}/versions/${shown}/entries/${encodeURIComponent(code)}/move`,
        { method: 'POST', body: { direction } },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });
  const saveGrid = useMutation({
    mutationFn: (batch: {
      body: BatchEntriesRequest;
      updateRefs: string[];
      createRefs: string[];
    }) =>
      apiRequest<EntryResponse[]>(`/tables/${tableKey}/versions/${shown}/entries/batch`, {
        method: 'POST',
        body: batch.body,
      }),
    onSuccess: async () => {
      setEdits({});
      setNewRows([]);
      setCellErrors({});
      setSaveError(null);
      setGridEdit(false);
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
    onError: (error, batch) => {
      // The errors by cell: «COS-0003.values.cost», «nuova-0.code», «creates.0.description».
      const fieldErrors = (error as { fieldErrors?: Record<string, string> }).fieldErrors ?? {};
      const marked: Record<string, string> = {};
      for (const [key, message] of Object.entries(fieldErrors)) {
        const parts = key.split('.');
        const head = parts[0] ?? '';
        let ref: string | undefined;
        let field: string;
        if (head === 'creates' || head === 'updates') {
          const index = Number(parts[1]);
          ref = head === 'creates' ? batch.createRefs[index] : batch.updateRefs[index];
          field = parts.slice(2).join('.');
        } else if (head.startsWith('nuova-')) {
          ref = batch.createRefs[Number(head.slice(6))];
          field = parts.slice(1).join('.');
        } else {
          ref = head;
          field = parts.slice(1).join('.');
        }
        if (ref) marked[`${ref}|${field}`] = message;
      }
      setCellErrors(marked);
      setSaveError(error.message);
    },
  });
  const deleteDraft = useMutation({
    mutationFn: (number: number) =>
      apiRequest<TableResponse>(`/tables/${tableKey}/versions/${number}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setShownNumber(null);
      setCompareWith(null);
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });

  const pendingCount = Object.keys(edits).length + newRows.length;
  const closeGrid = () => {
    if (pendingCount > 0 && !window.confirm('Le modifiche non salvate andranno perse: continui?')) {
      return;
    }
    setEdits({});
    setNewRows([]);
    setCellErrors({});
    setSaveError(null);
    setGridEdit(false);
  };
  const commands: (Command | false | null | undefined)[] = [
    editable &&
      !compareWith && {
        key: 'grid',
        label: gridEdit ? 'Blocca la tabella' : 'Modifica in tabella',
        icon: 'edit' as const,
        onClick: () => (gridEdit ? closeGrid() : setGridEdit(true)),
      },
    editable && {
      key: 'import',
      label: 'Importa da file',
      icon: 'upload' as const,
      onClick: () => setImporting(true),
    },
    editable && {
      key: 'entry',
      label: 'Nuova voce',
      icon: 'plus' as const,
      primary: true,
      onClick: () => {
        // A new voice belongs to the list shown.
        if (binding && currentList) setPrefill({ [binding.listColumn]: currentList });
        setEditing('new');
      },
    },
    canWrite &&
      data &&
      !hasDraft && {
        key: 'version',
        label: 'Nuova versione',
        icon: 'columns' as const,
        onClick: () => {
          if (shown !== null) newVersion.mutate(shown);
        },
      },
    {
      key: 'versions',
      label: showVersions ? 'Nascondi versioni' : 'Versioni',
      icon: 'history' as const,
      onClick: () => setShowVersions(!showVersions),
    },
    {
      key: 'export',
      label: 'Esporta CSV',
      icon: 'down' as const,
      disabled: !entries.data,
      onClick: () => {
        if (data && entries.data) {
          downloadText(`${data.key}-v${shown}.csv`, entriesToCsv(data.columns, entries.data));
        }
      },
    },
    {
      key: 'history',
      label: 'Storico',
      icon: 'history' as const,
      onClick: () => setShowHistory(true),
    },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  const changes = useMemo(
    () =>
      compareWith !== null && baseEntries.data && entries.data
        ? compareEntries(baseEntries.data, entries.data)
        : null,
    [compareWith, baseEntries.data, entries.data],
  );

  if (table.isPending) return <p className="muted">Caricamento…</p>;
  if (table.isError || !data) return <p className="error">{table.error?.message}</p>;

  const version = data.versions.find((item) => item.number === shown);
  // A list other than the Base shows its voices and the Base ones it does not replace.
  const listed =
    binding && currentList && !changes
      ? listEntries(entries.data ?? [], currentList, binding)
      : null;
  const fromBase = new Set(
    (listed ?? []).filter((item) => item.origin === 'base').map((item) => item.entry.code),
  );
  const isFromBase = (entry: EntryResponse) => !isBaseList && fromBase.has(entry.code);
  const fallbackOf = new Map(
    (listed ?? []).flatMap((item) => (item.fallback ? [[item.entry.code, item.fallback]] : [])),
  );
  const rows: EntryResponse[] = listed
    ? listed.map((item) => item.entry)
    : [
        ...(entries.data ?? []),
        ...(changes
          ? [...changes.values()].flatMap((change) =>
              change.kind === 'removed' ? [change.before] : [],
            )
          : []),
      ];
  const personalise = (entry: EntryResponse) => {
    if (!binding || !currentList) return;
    setPrefill({
      ...entry.values,
      [binding.listColumn]: currentList,
      [binding.replacesColumn]: entry.code,
    });
    setPrefillDescription(entry.description);
    setEditing('new');
  };
  const lists = binding
    ? (data.lookups[PRICE_LISTS_TABLE] ?? []).filter((list) => list.values?.kind === binding.kind)
    : [];

  // Tables on two levels: sub-entries under their parent, moved only among siblings.
  const parent = parentColumn(data.key, data.columns);
  const parentOf = (entry: EntryResponse) => {
    const value = parent ? entry.values[parent.key] : null;
    return typeof value === 'string' && value ? value : null;
  };
  const ordered = (entries.data ?? []).map((entry) => ({
    code: entry.code,
    parent: parentOf(entry),
    group: orderGroupOf(data.key, entry.values),
  }));
  const isChild = (entry: EntryResponse) =>
    ordered.some((item) => item.code === parentOf(entry) && !item.parent);
  const levelOf = (entry: EntryResponse): TypeLevel =>
    isChild(entry)
      ? 'child'
      : ordered.some((item) => item.parent === entry.code)
        ? 'parent'
        : 'single';
  const parentName = (entry: EntryResponse) => {
    const code = isChild(entry) ? parentOf(entry) : null;
    return code ? (entries.data?.find((item) => item.code === code)?.description ?? code) : null;
  };
  const addCost = (entry: EntryResponse) => {
    const type = isChild(entry) ? parentOf(entry)! : entry.code;
    const query = new URLSearchParams({ nuova: '1', type });
    if (isChild(entry)) query.set('subType', entry.code);
    navigate(`/tabelle/costi?${query.toString()}`);
  };
  // Tables whose entries go by group (the costs by type and sub-type): the
  // groups in the order of their types, the costs of the whole type first.
  const groupColumns = systemTable(data.key)?.orderGroups ?? [];
  const typeTable = data.columns.find((column) => column.key === groupColumns[0])?.lookup?.table;
  const typeEntries = typeTable ? (data.lookups[typeTable] ?? []) : [];
  const valueOf = (entry: EntryResponse, key: string | undefined) => {
    const value = key ? entry.values[key] : null;
    return typeof value === 'string' && value ? value : '';
  };
  const rank = (code: string) => {
    const index = typeEntries.findIndex((item) => item.code === code);
    return index < 0 ? typeEntries.length : index;
  };
  const gridRows =
    groupColumns.length > 0 && !changes
      ? rows
          .map((entry, index) => ({ entry, index }))
          .sort((a, b) => {
            const sub = (entry: EntryResponse) => {
              const code = valueOf(entry, groupColumns[1]);
              return code ? rank(code) : -1;
            };
            return (
              rank(valueOf(a.entry, groupColumns[0])) - rank(valueOf(b.entry, groupColumns[0])) ||
              sub(a.entry) - sub(b.entry) ||
              a.index - b.index
            );
          })
          .map((item) => item.entry)
      : rows;
  // Groups that open and close: a type over its sub-types, the costs under their type.
  const gridGroups: GridGroups<EntryResponse> | null = changes
    ? null
    : parent
      ? {
          of: (entry) =>
            isChild(entry) ? parentOf(entry) : levelOf(entry) === 'parent' ? entry.code : null,
          isHead: (entry) => !isChild(entry),
        }
      : groupColumns.length > 0
        ? {
            of: (entry) => valueOf(entry, groupColumns[0]) || '—',
            label: (group, items) => (
              <>
                <strong>
                  {group === '—'
                    ? binding?.kind === 'Passività'
                      ? 'Costi trasversali e fee'
                      : 'Senza tipologia'
                    : (typeEntries.find((item) => item.code === group)?.description ?? group)}
                </strong>
                <span className="muted">
                  {items.length} {items.length === 1 ? 'voce' : 'voci'}
                </span>
              </>
            ),
          }
        : null;

  const moveButton = (entry: EntryResponse, direction: MoveDirection) => (
    <button
      type="button"
      className="small"
      aria-label={`Sposta ${direction === 'up' ? 'su' : 'giù'} ${entry.description}`}
      title={
        isChild(entry)
          ? 'Sposta dentro il suo gruppo'
          : parent
            ? 'Sposta con le sue sottovoci'
            : undefined
      }
      disabled={move.isPending || moveEntry(ordered, entry.code, direction) === null}
      onClick={() => move.mutate({ code: entry.code, direction })}
    >
      {direction === 'up' ? '↑' : '↓'}
    </button>
  );

  const cell = (entry: EntryResponse, field: string, text: ReactNode, before?: ReactNode) => {
    const change = changes?.get(entry.code);
    return change?.kind === 'changed' && change.fields.includes(field) ? (
      <>
        <del>{before}</del> {text}
      </>
    ) : (
      text
    );
  };

  // --- The grid unlocked ------------------------------------------------------
  const newEntry = (row: NewRow): EntryResponse => ({
    code: row.key,
    description: row.description,
    sortOrder: 0,
    active: true,
    isDefault: false,
    hidden: false,
    locked: false,
    values: row.values,
  });
  const isNew = (entry: EntryResponse) => newRows.some((row) => row.key === entry.code);
  const editRows: EntryResponse[] = gridEdit ? [...gridRows, ...newRows.map(newEntry)] : gridRows;
  const rowIndex = new Map(editRows.map((entry, index) => [entry.code, index]));
  const editedValues = (entry: EntryResponse): Record<string, EntryValue> => {
    const row = newRows.find((item) => item.key === entry.code);
    return row ? row.values : { ...entry.values, ...(edits[entry.code]?.values ?? {}) };
  };
  const editedDescription = (entry: EntryResponse) =>
    newRows.find((item) => item.key === entry.code)?.description ??
    edits[entry.code]?.description ??
    entry.description;
  const setCell = (entry: EntryResponse, field: string, value: EntryValue | string) => {
    setCellErrors((current) => {
      const next = { ...current };
      delete next[`${entry.code}|${field}`];
      return next;
    });
    if (isNew(entry)) {
      setNewRows((rows) =>
        rows.map((row) => {
          if (row.key !== entry.code) return row;
          if (field === 'description') return { ...row, description: String(value ?? '') };
          if (field === 'code') return { ...row, code: String(value ?? '').toUpperCase() };
          return { ...row, values: { ...row.values, [field.slice(7)]: value as EntryValue } };
        }),
      );
      return;
    }
    setEdits((current) => {
      const before = current[entry.code] ?? {};
      const next: CellEdits =
        field === 'description'
          ? { ...before, description: String(value ?? '') }
          : {
              ...before,
              values: { ...(before.values ?? {}), [field.slice(7)]: value as EntryValue },
            };
      // Back to the saved value: nothing to save for this voice.
      const same =
        (next.description === undefined || next.description === entry.description) &&
        Object.entries(next.values ?? {}).every(
          ([key, item]) => String(item ?? '') === String(entry.values[key] ?? ''),
        );
      const rest = { ...current };
      if (same) delete rest[entry.code];
      else rest[entry.code] = next;
      return rest;
    });
  };
  const revertCell = (entry: EntryResponse, field: string) => {
    if (isNew(entry)) return;
    setCell(
      entry,
      field,
      field === 'description' ? entry.description : (entry.values[field.slice(7)] ?? null),
    );
  };
  const isChanged = (entry: EntryResponse, field: string) => {
    if (isNew(entry)) return true;
    const edit = edits[entry.code];
    if (!edit) return false;
    return field === 'description'
      ? edit.description !== undefined
      : edit.values !== undefined && field.slice(7) in edit.values;
  };
  const submitGrid = () => {
    const updates: BatchEntriesRequest['updates'] = [];
    const creates: NonNullable<BatchEntriesRequest['creates']> = [];
    const updateRefs: string[] = [];
    const createRefs: string[] = [];
    for (const [code, edit] of Object.entries(edits)) {
      const entry = rows.find((item) => item.code === code);
      if (!entry) continue;
      if (binding && currentList && isFromBase(entry)) {
        // A voice of the Base changed in a client list: it becomes a voice of the list.
        creates.push({
          description: edit.description ?? entry.description,
          values: {
            ...entry.values,
            ...(edit.values ?? {}),
            [binding.listColumn]: currentList,
            [binding.replacesColumn]: entry.code,
          },
        });
        createRefs.push(code);
        continue;
      }
      updates.push({
        code,
        ...(edit.description !== undefined ? { description: edit.description } : {}),
        ...(edit.values ? { values: { ...entry.values, ...edit.values } } : {}),
      });
      updateRefs.push(code);
    }
    for (const row of newRows) {
      creates.push({
        ...(row.code ? { code: row.code } : {}),
        description: row.description,
        values: row.values,
      });
      createRefs.push(row.key);
    }
    saveGrid.mutate({ body: { updates, creates }, updateRefs, createRefs });
  };
  const addRow = () =>
    setNewRows((current) => [
      ...current,
      {
        key: `nuova-${Date.now()}-${current.length}`,
        code: '',
        description: '',
        values: binding && currentList ? { [binding.listColumn]: currentList } : {},
      },
    ]);
  const editCell = (entry: EntryResponse, column: TableColumn) => (
    <GridCellInput
      column={column}
      value={editedValues(entry)[column.key] ?? null}
      rowValues={editedValues(entry)}
      lookups={data.lookups}
      row={rowIndex.get(entry.code) ?? 0}
      changed={isChanged(entry, `values.${column.key}`)}
      {...(cellErrors[`${entry.code}|values.${column.key}`]
        ? { error: cellErrors[`${entry.code}|values.${column.key}`] }
        : {})}
      label={`${column.label} di ${editedDescription(entry) || 'nuova voce'}`}
      {...(column.key === binding?.amountColumn && fallbackOf.get(entry.code)
        ? {
            placeholder: `Base ${formatValue(column, fallbackOf.get(entry.code)!.values[column.key], false, data.lookups)}`,
          }
        : {})}
      onChange={(value) => setCell(entry, `values.${column.key}`, value)}
      onRevert={() => revertCell(entry, `values.${column.key}`)}
    />
  );
  const textCell = (entry: EntryResponse, field: 'description' | 'code') => {
    const error = cellErrors[`${entry.code}|${field}`];
    const value =
      field === 'description'
        ? editedDescription(entry)
        : (newRows.find((row) => row.key === entry.code)?.code ?? '');
    return (
      <input
        className={['grid-cell', isChanged(entry, field) ? 'changed' : '', error ? 'invalid' : '']
          .join(' ')
          .trim()}
        data-row={rowIndex.get(entry.code) ?? 0}
        data-col={field}
        aria-label={field === 'description' ? 'Descrizione' : 'Codice'}
        aria-invalid={error ? true : undefined}
        title={error}
        value={value}
        placeholder={
          field === 'code' && data.autoCode ? `${data.autoCode}-… automatico` : undefined
        }
        onChange={(event) => setCell(entry, field, event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') revertCell(entry, field);
        }}
      />
    );
  };

  const columns: GridColumn<EntryResponse>[] = [
    {
      key: 'code',
      label: 'Codice',
      required: true,
      value: (entry) => entry.code,
      render: (entry) => (
        <span className="code">
          {entry.code}
          {entry.locked && (
            <span
              title="Voce di base: il codice regge le regole dei moduli"
              aria-label="Voce di base"
            >
              🔒
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'description',
      label: 'Descrizione',
      required: true,
      value: (entry) => entry.description,
      render: (entry) => {
        const change = changes?.get(entry.code);
        const button = (
          <button type="button" className="link strong" onClick={() => setEditing(entry)}>
            {cell(
              entry,
              'description',
              entry.description,
              change?.kind === 'changed' ? change.before.description : null,
            )}
          </button>
        );
        return isChild(entry) ? (
          <span className="entry-child">
            <span aria-hidden="true">↳</span> {button}
          </span>
        ) : (
          button
        );
      },
    },
    ...data.columns.map((column): GridColumn<EntryResponse> => ({
      key: `values.${column.key}`,
      label: column.label,
      align: column.type === 'number' || column.type === 'money' ? 'right' : undefined,
      // The list is the one chosen above the grid; what a voice replaces repeats its name.
      hiddenByDefault: column.key === binding?.listColumn || column.key === binding?.replacesColumn,
      value: (entry) => formatValue(column, entry.values[column.key], false, data.lookups),
      render: (entry) => {
        const change = changes?.get(entry.code);
        // A voice of the list not filled yet counts with the amount of the Base.
        const fallback = fallbackOf.get(entry.code);
        if (fallback && column.key === binding?.amountColumn && !entry.values[column.key]) {
          return (
            <span className="muted">
              {formatValue(column, fallback.values[column.key], false, data.lookups) || '—'}{' '}
              <span className="pill muted">dal Base</span>
            </span>
          );
        }
        return cell(
          entry,
          `values.${column.key}`,
          formatValue(column, entry.values[column.key], false, data.lookups),
          change?.kind === 'changed'
            ? formatValue(column, change.before.values[column.key], false, data.lookups) || '—'
            : null,
        );
      },
    })),
    ...(coverage.data
      ? [
          {
            key: 'costs',
            label: 'Costi',
            value: (entry: EntryResponse) => {
              const item = coverage.data[entry.code];
              return item ? coverageLabel(item, parentName(entry)).text : null;
            },
            render: (entry: EntryResponse) => {
              const item = coverage.data[entry.code];
              if (!item) return null;
              const label = coverageLabel(item, parentName(entry));
              return <span className={label.className}>{label.text}</span>;
            },
          },
        ]
      : []),
    {
      key: 'flags',
      label: 'Stato',
      value: (entry) => (entry.active ? 'Attiva' : 'Disattivata'),
      render: (entry) => (
        <span className="flags">
          <span className={entry.active ? 'pill ok' : 'pill muted'}>
            {entry.active ? 'Attiva' : 'Disattivata'}
          </span>
          {entry.isDefault && <span className="pill info">Predefinita</span>}
          {entry.hidden && <span className="pill muted">Nascosta</span>}
        </span>
      ),
    },
  ];

  return (
    <div className={showVersions && !gridEdit ? 'table-view with-versions' : 'table-view'}>
      <div className="stack">
        <div>
          <div className="row">
            <h2>{data.name}</h2>
            <span className={data.kind === 'system' ? 'pill brand' : 'pill info'}>
              {data.kind === 'system' ? 'di sistema' : 'libera'}
            </span>
            {data.isClassification && (
              <span className="pill muted">classificazione dei soggetti</span>
            )}
          </div>
          {data.description && <p className="muted">{data.description}</p>}
        </div>
        <dl className="table-meta">
          <div>
            <dt>Modifiche</dt>
            <dd>{data.mode === 'versioned' ? 'Per versioni con data' : 'Dirette, con storico'}</dd>
          </div>
          <div>
            <dt>Versione mostrata</dt>
            <dd>
              {version ? (
                <>
                  {version.number}{' '}
                  <span className={STATE_CLASS[version.state]}>
                    {VERSION_STATE_LABELS[version.state]}
                  </span>
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>Validità</dt>
            <dd>{version ? validity(version) : '—'}</dd>
          </div>
          <div>
            <dt>Origine</dt>
            <dd>{data.origin}</dd>
          </div>
        </dl>

        {changes ? (
          <div className="notice info diff-summary">
            <strong>
              Confronto della versione {shown} con la {compareWith}:
            </strong>{' '}
            {countOf(changes, 'added')} aggiunte · {countOf(changes, 'changed')} modificate ·{' '}
            {countOf(changes, 'removed')} rimosse
            <button type="button" className="small" onClick={() => setCompareWith(null)}>
              Chiudi il confronto
            </button>
          </div>
        ) : version?.state === 'draft' ? (
          <div className="notice">
            <strong>Bozza.</strong> Le modifiche non valgono finché non attivi la versione da una
            data.
            {canAdmin && (
              <button
                type="button"
                className="small primary"
                onClick={() => setActivating(version.number)}
              >
                Attiva…
              </button>
            )}
          </div>
        ) : version &&
          !editable &&
          canWrite &&
          data.mode === 'versioned' &&
          version.state === 'active' ? (
          <p className="notice info">
            Questa tabella cambia solo per versioni: usa «Nuova versione».
          </p>
        ) : version?.state === 'archived' ? (
          <p className="notice info">
            Versione archiviata: sola lettura. I documenti emessi quando era valida restano legati a
            lei.
          </p>
        ) : null}

        {binding && currentList && !changes && (
          <PriceListBar
            kind={binding.kind}
            lists={lists}
            current={currentList}
            canDuplicate={canWrite}
            onChoose={setChosenList}
          />
        )}
        {entries.isError && <p className="error">{entries.error.message}</p>}
        {move.error && <p className="error">{move.error.message}</p>}
        {gridEdit && (
          <div className="grid-edit-bar notice info" role="status">
            <span>
              <strong>Tabella sbloccata.</strong> Scrivi nelle celle: Invio scende alla riga sotto,
              Tab va a destra, Esc annulla la cella.{' '}
              {pendingCount > 0 &&
                `${pendingCount} ${pendingCount === 1 ? 'voce modificata' : 'voci modificate'}.`}
            </span>
            <span className="row">
              <button type="button" className="small" onClick={closeGrid}>
                {pendingCount > 0 ? 'Annulla le modifiche' : 'Blocca'}
              </button>
              <button
                type="button"
                className="small primary"
                disabled={pendingCount === 0 || saveGrid.isPending}
                onClick={submitGrid}
              >
                {saveGrid.isPending
                  ? 'Salvataggio…'
                  : `Salva ${pendingCount} ${pendingCount === 1 ? 'modifica' : 'modifiche'}`}
              </button>
            </span>
            {saveError && <span className="error block">{saveError}</span>}
          </div>
        )}
        <div onKeyDown={gridEdit ? moveOnEnter : undefined}>
          <DataGrid
            key={`${tableKey}-${shown}-${gridEdit ? 'edit' : 'view'}`}
            id={`table.${tableKey}`}
            columns={
              gridEdit
                ? columns.map((column) =>
                    column.key === 'code'
                      ? {
                          ...column,
                          render: (entry: EntryResponse) =>
                            isNew(entry) ? textCell(entry, 'code') : column.render(entry),
                        }
                      : column.key === 'description'
                        ? {
                            ...column,
                            render: (entry: EntryResponse) => textCell(entry, 'description'),
                          }
                        : column.key.startsWith('values.')
                          ? {
                              ...column,
                              render: (entry: EntryResponse) =>
                                editCell(
                                  entry,
                                  data.columns.find((item) => `values.${item.key}` === column.key)!,
                                ),
                            }
                          : column,
                  )
                : columns
            }
            rows={editRows}
            {...(gridEdit ? { pageSize: 5000 } : {})}
            {...(gridGroups && !gridEdit ? { groups: gridGroups } : {})}
            rowKey={(entry) => entry.code}
            searchPlaceholder="Cerca codice o descrizione"
            empty={editable ? 'Nessuna voce: aggiungi la prima con «Nuova voce».' : 'Nessuna voce.'}
            rowClassName={(entry) => {
              const change = changes?.get(entry.code);
              if (change) return `diff-${change.kind}`;
              if (isFromBase(entry) || fallbackOf.has(entry.code)) return 'inherited';
              return entry.active ? undefined : 'inactive';
            }}
            {...(gridEdit
              ? {}
              : {
                  onRowOpen: (entry: EntryResponse) =>
                    isFromBase(entry) ? editable && personalise(entry) : setEditing(entry),
                })}
            {...(editable && !changes && !gridEdit
              ? {
                  actions: (entry: EntryResponse, view: { natural: boolean }) =>
                    isFromBase(entry) ? (
                      <button type="button" className="small" onClick={() => personalise(entry)}>
                        Personalizza
                      </button>
                    ) : view.natural && (!binding || isBaseList) ? (
                      <span className="row-actions">
                        {moveButton(entry, 'up')}
                        {moveButton(entry, 'down')}
                      </span>
                    ) : null,
                }
              : {})}
          />
        </div>
        {gridEdit && (
          <div>
            <button type="button" className="small" onClick={addRow}>
              + Aggiungi riga
            </button>
          </div>
        )}
        {editable && !changes && !gridEdit && (
          <p className="muted small">
            {parent
              ? 'Le frecce cambiano l’ordine degli elenchi di scelta: una voce di primo livello si sposta con le sue sottovoci, una sottovoce dentro il suo gruppo.'
              : 'Le frecce cambiano l’ordine degli elenchi di scelta.'}{' '}
            Con una ricerca o un ordinamento per colonna le frecce si nascondono.
          </p>
        )}
      </div>

      {/* The grid unlocked takes the whole width. */}
      {showVersions && !gridEdit && (
        <aside className="card versions" aria-label="Versioni">
          <h3>Versioni</h3>
          {data.versions.map((item) => (
            <div
              key={item.number}
              className={item.number === shown ? 'version selected' : 'version'}
            >
              <div className="row">
                <strong>Versione {item.number}</strong>
                <span className={STATE_CLASS[item.state]}>{VERSION_STATE_LABELS[item.state]}</span>
              </div>
              <small className="muted">
                {validity(item)} · {item.entryCount} voci · {item.createdBy ?? '—'}
              </small>
              <div className="row">
                {item.number !== shown ? (
                  <>
                    <button
                      type="button"
                      className="small"
                      onClick={() => {
                        setShownNumber(item.number);
                        setCompareWith(null);
                      }}
                    >
                      Mostra
                    </button>
                    <button
                      type="button"
                      className="small"
                      onClick={() => setCompareWith(item.number)}
                    >
                      Confronta
                    </button>
                  </>
                ) : (
                  <small className="muted">mostrata</small>
                )}
                {canWrite && !hasDraft && item.state !== 'draft' && (
                  <button
                    type="button"
                    className="small"
                    onClick={() => newVersion.mutate(item.number)}
                  >
                    Nuova da questa
                  </button>
                )}
                {canAdmin && item.state === 'draft' && data.versions.length > 1 && (
                  <button
                    type="button"
                    className="small danger"
                    onClick={() => deleteDraft.mutate(item.number)}
                  >
                    Elimina bozza
                  </button>
                )}
              </div>
            </div>
          ))}
          {(newVersion.error ?? deleteDraft.error) && (
            <p className="error">{(newVersion.error ?? deleteDraft.error)!.message}</p>
          )}
          <p className="muted small">
            {data.mode === 'versioned'
              ? 'Si modifica una bozza e la si attiva da una data: i documenti restano legati alla versione con cui sono nati.'
              : 'Modifiche dirette con storico; una bozza serve per programmare un cambiamento da una data.'}
          </p>
        </aside>
      )}

      {editing && shown !== null && (
        <EntryDialog
          table={data}
          version={shown}
          entry={editing === 'new' ? null : editing}
          initialValues={editing === 'new' ? (prefill ?? undefined) : undefined}
          initialDescription={editing === 'new' ? (prefillDescription ?? undefined) : undefined}
          readOnly={
            !editable || changes?.get(editing === 'new' ? '' : editing.code)?.kind === 'removed'
          }
          onClose={() => {
            setEditing(null);
            setPrefill(null);
            setPrefillDescription(null);
          }}
        >
          {editing !== 'new' && coverage.data?.[editing.code] && (
            <LinkedCosts
              coverage={coverage.data[editing.code]!}
              level={levelOf(editing)}
              parentName={parentName(editing)}
              units={data.lookups['unita-misura'] ?? []}
              {...(canWrite ? { onAddCost: () => addCost(editing) } : {})}
            />
          )}
        </EntryDialog>
      )}
      {activating !== null && (
        <ActivateDialog
          table={data}
          number={activating}
          onClose={() => setActivating(null)}
          onActivated={() => {
            setActivating(null);
            setShownNumber(null);
            setCompareWith(null);
          }}
        />
      )}
      {showHistory && <HistoryDialog table={data} onClose={() => setShowHistory(false)} />}
      {importing && data && shown !== null && (
        <ImportEntriesDialog
          tableKey={data.key}
          tableName={data.name}
          columns={data.columns}
          lookups={data.lookups}
          autoCode={data.autoCode}
          versionNumber={shown}
          onClose={() => setImporting(false)}
          onDone={async () => {
            await queryClient.invalidateQueries({ queryKey: ['tables'] });
          }}
        />
      )}
    </div>
  );
}

function validity(version: VersionResponse): string {
  if (!version.validFrom) return 'data da decidere';
  return version.validTo
    ? `dal ${formatDate(version.validFrom)} al ${formatDate(version.validTo)}`
    : `dal ${formatDate(version.validFrom)}`;
}

function countOf(changes: Map<string, { kind: string }>, kind: string): number {
  return [...changes.values()].filter((change) => change.kind === kind).length;
}

function ActivateDialog({
  table,
  number,
  onClose,
  onActivated,
}: {
  table: TableResponse;
  number: number;
  onClose: () => void;
  onActivated: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const [validFrom, setValidFrom] = useState(italianToday());
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const activate = useMutation({
    mutationFn: () =>
      apiRequest<TableResponse>(`/tables/${table.key}/versions/${number}/activate`, {
        method: 'POST',
        body: { validFrom },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      onActivated();
    },
  });
  const current = table.versions.find((version) => version.state === 'active');

  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Attiva la versione {number}</h2>
        <p>
          Da questa data «{table.name}» usa la versione {number}.
          {current && ` La versione ${current.number} resta valida fino al giorno prima.`}
        </p>
        <Field label="Valida dal">
          <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </Field>
        {activate.error && <p className="error">{activate.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={activate.isPending}
            onClick={() => activate.mutate()}
          >
            Attiva
          </button>
        </div>
      </div>
    </dialog>
  );
}

function HistoryDialog({ table, onClose }: { table: TableResponse; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const history = useQuery({
    queryKey: ['tables', table.key, 'history'],
    queryFn: () => apiRequest<AuditEventResponse[]>(`/tables/${table.key}/history`),
  });
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Storico · {table.name}</h2>
        {history.isPending && <p className="muted">Caricamento…</p>}
        {history.data?.length === 0 && (
          <p className="muted">Nessuna modifica dopo il caricamento iniziale.</p>
        )}
        <ul className="timeline">
          {history.data?.map((event) => (
            <li key={event.id}>
              <span className="muted small">
                {formatTimestamp(event.occurredAt)}
                <br />
                {event.actorEmail ?? '—'}
              </span>
              <span>
                {event.summary}
                {describeAuditDetails(event).map((line) => (
                  <small key={line} className="muted block">
                    {line}
                  </small>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <div className="actions">
          <button type="button" className="primary" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </dialog>
  );
}
